import { HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { ErrorHandler, Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { Logger } from '@sondermind/datadog-logging';
import { LocalStorageService } from '@sondermind/local-storage';
import { NativeAppContextHelpers } from '@sondermind/native-app-interface';
import { SentryClient } from '@sondermind/sentry-client';
import { ApiError, API_ERROR_NAME } from '@sondermind/utilities/models-http';
import { Environment, ENVIRONMENT } from '@sondermind/utilities/models-environment';
import { VIDEO_SESSION_ACTIVE } from '@sondermind/constants';

export const SUPPRESS_ERRORS: RegExp[] = [
  /Firebase: Firebase App named '\[DEFAULT\]' already exists/,
  /Missing Stripe.js!/
];

// Chunk-load failures that should trigger a one-shot auto-reload. The first
// entry is the legacy webpack message; the rest are native ESM lazy-chunk
// failures emitted by @nx/angular:browser-esbuild on iOS/WebKit and other
// engines, none of which match the webpack pattern.
export const RELOAD_ERRORS: RegExp[] = [
  /ChunkLoadError: Loading chunk (.*) failed/,
  /Importing a module script failed/,
  /error loading dynamically imported module/,
  /Failed to fetch dynamically imported module/
];

// sessionStorage key recording the timestamp (ms) of the last auto-reload
// attempt. Guards against a reload loop when a chunk is genuinely gone.
export const CHUNK_RELOAD_ATTEMPTED_KEY = 'sm-chunk-reload-attempted';

// Only suppress a repeat auto-reload if the previous attempt was within this
// window. A genuinely-gone chunk reload-loops within a second or two, so the
// window breaks the loop; a distinct, later chunk failure in the same session
// falls outside the window and can still self-heal once. This avoids the
// guard permanently disabling recovery after a successful first reload.
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

/** Fingerprint path segment used when an ApiError carries no URL, so URL-less
 *  errors form an explicit, diagnosable bucket rather than an empty string. */
export const UNKNOWN_URL_PATH = 'unknown';

/** Collapses dynamic path segments (numeric ids, UUIDs) so the Sentry fingerprint
 *  groups by endpoint shape rather than fragmenting per-resource. */
const DYNAMIC_SEGMENT_PLACEHOLDER = ':id';

/** Matches a canonical UUID path segment. */
const UUID_SEGMENT: RegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Matches a purely numeric path segment (e.g. resource id `/appointments/12345`). */
const NUMERIC_SEGMENT: RegExp = /^\d+$/;

@Injectable({
  providedIn: 'root'
})
export class AppErrorHandler extends ErrorHandler {
  constructor(
    @Inject(ENVIRONMENT)
    private env: Environment,
    private localStorageService: LocalStorageService,
    private logger: Logger,
    @Inject(PLATFORM_ID)
    private platformId: object
  ) {
    super();
  }

  /**
   * Associates the Sentry scope with the current user by id only. `email` (and any other
   * PII) must never be forwarded to Sentry.setUser — see MER-5370.
   */
  handleUserChange(id: string | undefined): void {
    // if there is no id, clear scope
    if (!id) {
      SentryClient.configureScope((scope) => scope.clear());
    } else {
      // otherwise, set the user by id only
      SentryClient.setUser({ id });
    }
  }

  override handleError(err: any): void {
    const videoSessionActive = this.localStorageService.getItem(VIDEO_SESSION_ACTIVE);

    // reload the page on chunk loading errors (webpack + esbuild/ESM variants)
    const isChunkLoadError = RELOAD_ERRORS.some((pat) => pat.test(err?.message));

    // Only touch sessionStorage when we actually have a chunk error to handle.
    // This keeps the common error path off storage entirely, and confines the
    // storage access (which can throw in Safari/iOS private mode and sandboxed
    // iframes) to a branch that already fails open toward reloading.
    //
    // isPlatformBrowser gates the entire self-heal path first: this ErrorHandler
    // also runs in the provider-directory SSR build, where `window` /
    // `sessionStorage` / `location` do not exist on the Node server. Because `&&`
    // short-circuits, none of reloadRecentlyAttempted(), markReloadAttempted(), or
    // windowLocationReload() touch a browser object during server-side rendering.
    if (isPlatformBrowser(this.platformId) && isChunkLoadError && !this.reloadRecentlyAttempted()) {
      if (!videoSessionActive) {
        // log the recovery announcement at warn: a self-healing reload is not an
        // error, and logging it at 'error' double-counts the same event we also
        // avoid re-capturing to Sentry below. Scoped to this branch because only
        // the non-video path actually reloads.
        this.logger.log('Chunk loading error, reloading', undefined, 'warn');
        // loop guard: record this attempt so a genuinely-gone chunk cannot
        // reload-loop, while a distinct later failure can still self-heal.
        this.markReloadAttempted();
        this.windowLocationReload();
        // A successful non-video self-heal is fully handled; short-circuit before
        // captureException so the reload is not double-captured to Sentry.
        return;
      }
      // To help debug video session issues
      this.logger.error('Chunk loading error while in video session', err);
    }

    const suppressed = SUPPRESS_ERRORS.some((pat) => err.message?.match(pat));

    // ignore errors defined in SUPPRESS_ERRORS
    if (err.message && suppressed) {
      return;
    }

    // format http errors slightly differently
    const errorToCapture = err instanceof HttpErrorResponse
      ? `HTTP Error ${err.status} - ${err.message}: ${err.url}`
      : err.originalError || err.error || err;

    // Add tag for whether the user is in native mobile app
    const isNativeApp = NativeAppContextHelpers.isHostedInNativeApp().toString();

    // include special indicator if error occurs during video session.
    const tags: Record<string, string> = videoSessionActive
      ? { videoSessionActive: 'true', isNativeApp }
      : { isNativeApp };

    const captureContext: { tags: Record<string, string>; fingerprint?: string[]; } = { tags };

    // Give ApiErrors a bespoke fingerprint keyed on {status, sanitized path} so
    // distinct endpoints split out of the catch-all ApiError Sentry group. HTTP
    // failures reach this global handler as ApiError (HttpErrorService rethrows
    // every HttpErrorResponse as one), not as a raw HttpErrorResponse — Sentry
    // groups by the ApiError name, so keying on ApiError is what actually splits
    // the group. Duck-type on the name (matching sentry.client.ts) because
    // instanceof can fail across lazy-loaded bundles with duplicated identity.
    const apiError = this.asApiError(err);
    if (apiError) {
      captureContext.fingerprint = [
        API_ERROR_NAME,
        String(apiError.status),
        this.sanitizeUrlPath(apiError.url)
      ];
    }

    SentryClient.captureException(errorToCapture, captureContext);

    if (!this.env.production) {
      super.handleError(err);
    }
  }

  /**
   * Narrows an unknown error to an ApiError. Duck-types on the error name rather
   * than using `instanceof` so it still matches ApiErrors thrown from lazy-loaded
   * bundles with duplicated module identity (same rationale as sentry.client.ts).
   */
  private asApiError(err: unknown): ApiError | undefined {
    if (err instanceof ApiError) {
      return err;
    }
    if (err instanceof Error && err.name === API_ERROR_NAME) {
      // Cross-bundle identity mismatch: shape matches ApiError even though
      // instanceof failed. Its status/url fields carry the fingerprint inputs.
      return err as ApiError;
    }
    return undefined;
  }

  /**
   * Reduces an ApiError URL to a stable endpoint shape: origin and query string
   * are stripped, and dynamic path segments (numeric ids, UUIDs) are collapsed to
   * a placeholder. This groups the Sentry fingerprint by endpoint without leaking
   * tokens/PII and without re-fragmenting per-resource on dynamic path values.
   */
  private sanitizeUrlPath(url: string | null | undefined): string {
    if (!url) {
      return UNKNOWN_URL_PATH;
    }

    let path: string;
    try {
      path = new URL(url).pathname;
    } catch {
      // relative or malformed URL: strip query string and hash fragment manually
      [path] = url.split(/[?#]/);
    }

    return this.normalizeDynamicSegments(path);
  }

  /**
   * Replaces numeric- and UUID-shaped path segments with a placeholder so
   * `/api/v2/appointments/12345` and `/api/v2/appointments/98765` collapse into a
   * single fingerprint instead of fragmenting the Sentry group per resource id.
   */
  private normalizeDynamicSegments(path: string): string {
    return path
      .split('/')
      .map((segment) => {
        if (NUMERIC_SEGMENT.test(segment) || UUID_SEGMENT.test(segment)) {
          return DYNAMIC_SEGMENT_PLACEHOLDER;
        }
        return segment;
      })
      .join('/');
  }

  /**
   * Reloads the window location, abstracted for easier unit testing.
   */
  windowLocationReload(): void {
    window.location.reload();
  }

  /**
   * True if an auto-reload was attempted within CHUNK_RELOAD_WINDOW_MS. Storage
   * access is wrapped because sessionStorage can throw in Safari/iOS private
   * mode and sandboxed iframes; a throw fails open (treated as "not attempted")
   * so recovery is never worse than before this guard existed.
   */
  private reloadRecentlyAttempted(): boolean {
    try {
      const last = window.sessionStorage.getItem(CHUNK_RELOAD_ATTEMPTED_KEY);
      if (!last) {
        return false;
      }
      const elapsed = Date.now() - Number(last);
      return Number.isFinite(elapsed) && elapsed >= 0 && elapsed < CHUNK_RELOAD_WINDOW_MS;
    } catch {
      return false;
    }
  }

  /**
   * Records the current time as the last auto-reload attempt. Swallows storage
   * failures (see reloadRecentlyAttempted) so the subsequent reload still fires.
   */
  private markReloadAttempted(): void {
    try {
      window.sessionStorage.setItem(CHUNK_RELOAD_ATTEMPTED_KEY, String(Date.now()));
    } catch {
      // sessionStorage unavailable (e.g. private mode) — proceed with reload.
    }
  }
}
